import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../../layouts/Layout";
import { SearchOutlined, BellOutlined } from "@ant-design/icons";
import { useSecureService } from "../../store/service";
import { List, Spin, Button } from "antd";
import { useAtom } from "jotai";
import { userAtom } from "../../store/atoms";
import SearchResultItem from "../../components/SearchResultItem/SearchResultItem";
import EntryResultItem from "../../components/EntryResultItem/EntryResultItem";
import NotificationResultItem from "../../components/NotificationResultItem/NotificationResultItem";
import styles from "./Results.module.scss";
import NoDataComponent from "../../components/NoDataComponent/NoDataComponent";

const PREVIOUS_NOTIFICATIONS_PAGE_SIZE = 10;

interface ResultsProps {
  type?: 'search' | 'notifications' | 'entry';
  query?: string;
}

const Results: React.FC<ResultsProps> = ({ type = 'search', query = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userState, setUserState] = useAtom(userAtom);
  const {  getUserNotifications, changeNotificationStatus, getEntrySummary } = useSecureService();
  const [loading, setLoading] = useState(true);
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [entryLoading, setEntryLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState(query);
  const [pageType, setPageType] = useState(type);
  const [markingReadId, setMarkingReadId] = useState<number | null>(null);
  const [notificationFilter, setNotificationFilter] = useState<'all' | 'today' | 'thisWeek' | 'earlier'>('all');
  const [readVisibleCount, setReadVisibleCount] = useState(PREVIOUS_NOTIFICATIONS_PAGE_SIZE);
  const readVisibleCountRef = useRef(readVisibleCount);
  const readListLengthRef = useRef(0);

  // URL'den parametreleri al
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlType = searchParams.get('type') as 'search' | 'notifications' | 'entry' || 'search';
    const urlQuery = searchParams.get('q') || '';
    const urlStatus = searchParams.get('status') || '';
    const urlStartDate = searchParams.get('startDate') || '';
    const urlScope = searchParams.get('scope') as 'Internal' | 'External' | null;
    setPageType(urlType);
    setSearchQuery(urlQuery);
    // Entry tipi için veri yükle (scope: Internal | External)
    if (urlType === 'entry') {
      loadEntryResults(urlStatus, urlStartDate, urlScope || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEntryResults on url change only
  }, [location.search]);

  // Search results için
  const loadSearchResults = async () => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      // TODO: Search API'si implement edildiğinde buraya eklenecek
      setResults([]);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Entry results için (scope: Internal | External)
  const loadEntryResults = async (status?: string, startDate?: string, scope?: 'Internal' | 'External') => {
    setEntryLoading(true);
    try {
      const response = await getEntrySummary(status, startDate, scope);
      if (response.success && response.data?.items) {
        setResults(response.data.items);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Entry results error:', error);
      setResults([]);
    } finally {
      setEntryLoading(false);
    }
  };

  // Notifications için
  const loadNotifications = async () => {
    setNotificationLoading(true);
    try {
      const response = await getUserNotifications();
      if (response.success && response.data?.notifications) {
        setResults(response.data.notifications);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Notifications error:', error);
      setResults([]);
    } finally {
      setNotificationLoading(false);
    }
  };

  useEffect(() => {
    if (pageType === 'search') {
      loadSearchResults();
    } else if (pageType === 'notifications') {
      loadNotifications();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSearchResults/loadNotifications stable
  }, [pageType, searchQuery]);

  // Ana loading state'ini güncelle
  useEffect(() => {
    setLoading(searchLoading || entryLoading || notificationLoading);
  }, [searchLoading, entryLoading, notificationLoading]);

  const handleItemClick = (item: any) => {
    if (pageType === 'notifications' && item.notificationTarget) {
      navigate(`/${item.notificationTarget}`);
    } else if (pageType === 'search') {
      navigate(`/${item.path}`);
    } else if (pageType === 'entry') {
      navigate(`/${item.path}`);
    }
  };

  const handleEntryNameClick = (target: string, notificationId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const item = results.find((r: any) => r.id === notificationId);
    navigate(`/${target}`);
    if (!item || item.isRead) return;
    void (async () => {
      try {
        const response = await changeNotificationStatus([notificationId], true);
        if (response.success) {
          setUserState((prev: typeof userState) =>
            prev
              ? {
                  ...prev,
                  unreadNotificationCount: Math.max(
                    0,
                    (prev.unreadNotificationCount || 0) - 1
                  ),
                }
              : prev
          );
        } else {
          console.error("Error marking notification as read:", response.message);
        }
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    })();
  };

  const handleMarkSingleAsRead = async (itemId: number) => {
    if (markingReadId !== null) return;
    setMarkingReadId(itemId);
    try {
      const response = await changeNotificationStatus([itemId], true);
      if (response.success) {
        await loadNotifications();
        if (userState) {
          setUserState({
            ...userState,
            unreadNotificationCount: Math.max(0, (userState.unreadNotificationCount || 0) - 1),
          });
        }
      } else {
        console.error('Error marking notification as read:', response.message);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleMarkAsRead = async () => {
    const unreadIds = filteredResults
      .filter((item: any) => !item.isRead)
      .map((item: any) => item.id);
    if (unreadIds.length === 0) return;

    setMarkAsReadLoading(true);
    try {
      const response = await changeNotificationStatus(unreadIds, true);
      if (response.success) {
        await loadNotifications();
        if (userState) {
          setUserState({
            ...userState,
            unreadNotificationCount: Math.max(0, (userState.unreadNotificationCount || 0) - unreadIds.length)
          });
        }
      } else {
        console.error('Error marking as read:', response.message);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  const filteredResults = useMemo(() => {
    if (pageType !== 'notifications' || notificationFilter === 'all') return results;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - (dayOfWeek - 1));

    return results.filter((item: any) => {
      const raw = item.createdAt;
      const dateStr = raw && !raw.includes('Z') ? raw + 'Z' : raw;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;

      if (notificationFilter === 'today') return d >= startOfToday;
      if (notificationFilter === 'thisWeek') return d >= startOfWeek;
      if (notificationFilter === 'earlier') return d < startOfWeek;
      return true;
    });
  }, [pageType, notificationFilter, results]);

  const { notificationUnread, notificationRead } = useMemo(() => {
    if (pageType !== 'notifications') {
      return { notificationUnread: [] as any[], notificationRead: [] as any[] };
    }
    const unread = filteredResults.filter((item: any) => !item.isRead);
    const read = filteredResults.filter((item: any) => item.isRead);
    return { notificationUnread: unread, notificationRead: read };
  }, [pageType, filteredResults]);

  const previousNotificationsVisible = useMemo(
    () => notificationRead.slice(0, readVisibleCount),
    [notificationRead, readVisibleCount]
  );

  readListLengthRef.current = notificationRead.length;
  readVisibleCountRef.current = readVisibleCount;

  useEffect(() => {
    setReadVisibleCount(PREVIOUS_NOTIFICATIONS_PAGE_SIZE);
  }, [notificationFilter, pageType]);

  /** Previous list: scroll ile + içerik kısaysa (scrollbar yok) otomatik 10'ar yükle */
  useEffect(() => {
    if (pageType !== 'notifications') return;

    let raf = 0;

    const appendChunk = () => {
      setReadVisibleCount((c) => {
        const cap = readListLengthRef.current;
        if (c >= cap) return c;
        return Math.min(c + PREVIOUS_NOTIFICATIONS_PAGE_SIZE, cap);
      });
    };

    const shouldLoadMore = () => {
      if (readVisibleCountRef.current >= readListLengthRef.current) return false;

      const mainScroll = document.querySelector(
        '[data-app-scroll-lock]'
      ) as HTMLElement | null;

      if (mainScroll) {
        const { scrollTop, clientHeight, scrollHeight } = mainScroll;
        const gap = scrollHeight - scrollTop - clientHeight;
        if (gap < 200 || scrollHeight <= clientHeight + 12) return true;
      }

      const doc = document.documentElement;
      const winGap = doc.scrollHeight - window.scrollY - window.innerHeight;
      if (winGap < 200 || doc.scrollHeight <= window.innerHeight + 12) return true;

      return false;
    };

    const check = () => {
      if (shouldLoadMore()) appendChunk();
    };

    const scheduleCheck = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(check);
    };

    const mainScroll = document.querySelector(
      '[data-app-scroll-lock]'
    ) as HTMLElement | null;
    mainScroll?.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('resize', scheduleCheck);

    scheduleCheck();
    const t1 = requestAnimationFrame(() => scheduleCheck());
    const t2 = setTimeout(scheduleCheck, 100);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(t1);
      clearTimeout(t2);
      mainScroll?.removeEventListener('scroll', scheduleCheck);
      window.removeEventListener('scroll', scheduleCheck);
      window.removeEventListener('resize', scheduleCheck);
    };
  }, [pageType, notificationRead.length, readVisibleCount]);

  const getPageTitle = () => {
    if (pageType === 'search') {
      return searchQuery ? `Search results for "${searchQuery}"` : 'Search results';
    } else if (pageType === 'entry') {
      // URL'den direkt oku, pageType state'ine güvenme
      const searchParams = new URLSearchParams(location.search);
      const status = searchParams.get('status');

      if (status) {
        return `${status}`;
      }
      return 'All Projects';
    } else {
      return 'Notifications';
    }
  };

  const getPageIcon = () => {
    if (pageType === 'search') {
      return <SearchOutlined />;
    } else if (pageType === 'entry') {
      return <SearchOutlined />;
    } else {
      return <BellOutlined />;
    }
  };
  
  return (
    <MainLayout title={getPageTitle()} icon={getPageIcon()}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: '#999' }}>
            {searchLoading ? 'Searching...' : 
             entryLoading ? 'Loading projects...' : 
             notificationLoading ? 'Loading notifications...' : 
             'Loading...'}
          </div>
        </div>
      ) : results.length > 0 ? (
        <>
          {pageType === 'notifications' && (
            <div className={styles.notificationToolbar}>
              <div className={styles.filterTabs}>
                {([
                  { key: 'all', label: 'All' },
                  { key: 'today', label: 'Today' },
                  { key: 'thisWeek', label: 'This Week' },
                  { key: 'earlier', label: 'Earlier' },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`${styles.filterTab} ${notificationFilter === tab.key ? styles.filterTabActive : ''}`}
                    onClick={() => setNotificationFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className={styles.toolbarMarkReadWrap}>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleMarkAsRead}
                  disabled={markAsReadLoading || !filteredResults.some((item: any) => !item.isRead)}
                  style={{
                    backgroundColor: 'var(--app-accent-color)',
                    borderColor: 'var(--app-accent-color)',
                    color: '#F6FFED',
                  }}
                  className={`${styles.markReadButton} ${markAsReadLoading || !filteredResults.some((item: any) => !item.isRead) ? styles.disabledButton : ''}`}
                >
                  {markAsReadLoading ? 'Marking...' : 'Mark All as Read'}
                </Button>
              </div>
            </div>
          )}
          {searchLoading && pageType === 'search' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#999' }}>Arama sonuçları yükleniyor...</div>
            </div>
          ) : entryLoading && pageType === 'entry' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#999' }}>Projeler yükleniyor...</div>
            </div>
          ) : notificationLoading && pageType === 'notifications' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#999' }}>Bildirimler yükleniyor...</div>
            </div>
          ) : filteredResults.length > 0 ? (
            pageType === 'notifications' ? (
                <>
                  {notificationUnread.length > 0 && (
                    <List
                      className={styles.resultsList}
                      dataSource={notificationUnread}
                      renderItem={(item) => (
                        <NotificationResultItem
                          item={item}
                          onMarkAsRead={handleMarkSingleAsRead}
                          markingAsRead={markingReadId === item.id}
                          onEntryNameClick={handleEntryNameClick}
                        />
                      )}
                    />
                  )}
                  {notificationRead.length > 0 && (
                    <>
                      <div
                        className={`${styles.sectionHeaderWrap} ${notificationUnread.length > 0 ? styles.sectionHeaderWrapWithDivider : ''}`}
                      >
                        <div className={styles.sectionHeaderInner}>
                          <span>Previous Notifications</span>
                        </div>
                      </div>
                      <List
                        className={styles.resultsList}
                        dataSource={previousNotificationsVisible}
                        renderItem={(item) => (
                          <NotificationResultItem
                            item={item}
                            onMarkAsRead={handleMarkSingleAsRead}
                            markingAsRead={markingReadId === item.id}
                            onEntryNameClick={handleEntryNameClick}
                          />
                        )}
                      />
                    </>
                  )}
                </>
            ) : (
              <List
                className={styles.resultsList}
                dataSource={filteredResults}
                renderItem={(item) => {
                  if (pageType === 'search') {
                    return <SearchResultItem item={item} onItemClick={handleItemClick} />;
                  } else {
                    return <EntryResultItem item={item} onItemClick={handleItemClick} />;
                  }
                }}
              />
            )
          ) : (
            <div className={styles.emptyState}>
              <img src="/images/icon/notificationbell.svg" alt="" className={styles.emptyIcon} />
              <div className={styles.emptyTitle}>No new notifications</div>
              <div className={styles.emptyDescription}>You're all caught up! Check back later for new updates.</div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          {pageType === 'notifications' ? (
            <>
              <img src="/images/icon/notificationbell.svg" alt="" className={styles.emptyIcon} />
              <div className={styles.emptyTitle}>No new notifications</div>
              <div className={styles.emptyDescription}>You're all caught up! Check back later for new updates.</div>
            </>
          ) : (
            <NoDataComponent message="No Data Found!" />
          )}
        </div>
      )}
    </MainLayout>
  );
};

export default Results; 